"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFields = void 0;
const core_1 = require("../core");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const model_1 = require("../model");
const context_1 = require("../context");
const use_meta_1 = require("./use_meta");
const use_field_1 = require("./use_field");
const store_1 = require("../store");
function useFields(param1, param2, param3) {
    const { datasheetId: metaDatasheetId } = (0, use_meta_1.useMeta)();
    const hasDatasheet = param1 instanceof model_1.Datasheet;
    const datasheetId = hasDatasheet ? param1.datasheetId : metaDatasheetId;
    const usedDatasheetId = hasDatasheet ? datasheetId : undefined;
    const viewId = (hasDatasheet ? param2 : param1);
    const query = hasDatasheet ? param3 : param2;
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    const columns = (0, react_redux_1.useSelector)(state => { var _a; return (_a = (0, store_1.getView)(state, viewId, usedDatasheetId)) === null || _a === void 0 ? void 0 : _a.columns; }, react_redux_1.shallowEqual);
    const fieldMap = (0, react_redux_1.useSelector)(state => (0, store_1.getFieldMap)(state, usedDatasheetId), react_redux_1.shallowEqual);
    const fieldPermissionMap = (0, react_redux_1.useSelector)(state => (0, store_1.getFieldPermissionMap)(state, usedDatasheetId));
    return (0, react_1.useMemo)(() => {
        if (!viewId || !datasheetId || !fieldMap || !columns) {
            return [];
        }
        let _columns = columns;
        if (query && 'ids' in query) {
            if (!query.ids) {
                _columns = [];
            }
            const idSet = new Set(query.ids);
            _columns = _columns.filter(col => idSet.has(col.fieldId));
        }
        if (query && 'visible' in query && query.visible) {
            _columns = _columns.filter(col => !col.hidden);
        }
        return _columns.map(({ fieldId }) => {
            const fieldRole = core_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, fieldId);
            return new model_1.Field(datasheetId, context, fieldRole === core_1.ConfigConstant.Role.None ? (0, use_field_1.createDeniedField)() : fieldMap[fieldId]);
        });
    }, [viewId, datasheetId, fieldMap, columns, query, fieldPermissionMap, context]);
}
exports.useFields = useFields;
